import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Alert } from "@models/alerts/alert";
import { EntityType } from '@models/common/entity-type.enum';
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { IndividualRecord } from '@models/individuals/Individual-record';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  IndividualMergeModalComponent
} from '@pages/individuals/components/individuals-page/components/individual-merge-modal/individual-merge-modal.component';
import { IndividualsExportService } from "@pages/individuals/services/individuals-export.service";
import { IndividualsGridService } from "@pages/individuals/services/individuals-grid.service";
import {
  ManageTagsModalComponent
} from '@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.component';
import { AlertsListComponent } from "@shared/components/alerts-list/alerts-list.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { ConfirmModalComponent } from '@shared/components/commons/confirm/confirm-modal.component';
import {
  AgIndividualActionsComponent
} from '@shared/components/grid/ag-cells/individuals/ag-individual-actions/ag-individual-actions.component';
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { NotesComponent } from "@shared/components/notes/notes.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { IPartialHeaderData } from "@shared/services/header.service";
import { OrganisationSettingsService } from "@shared/services/organisation-settings.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { TagsService } from '@shared/services/tags.service';
import { doNothing } from "@shared/utils/doNothing";
import { dateComparator } from '@shared/utils/grid/date-comparator';
import { getIndividualProfilePath } from "@shared/utils/route-path-utils";
import { GetRowIdFunc, GetRowIdParams, GridApi } from "ag-grid-community";
import { ToastrService } from 'ngx-toastr';
import { delay, of, take } from 'rxjs';
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";

@Component({
  selector: 'app-individuals',
  standalone: true,
  templateUrl: 'individuals-page.component.html',
  styleUrl: 'individuals-page.component.scss',
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    ButtonComponent,
    NotesComponent,
    AlertsListComponent,
    HasRoleDirective,
    NgbDropdownItem,
    GridServerSideComponent,
  ],
  providers: [
    IndividualsGridService,
    IndividualsExportService
  ]
})
export class IndividualsPageComponent extends CommonPageComponent implements OnInit {
  individualGridService = inject(IndividualsGridService);
  individualsExportService = inject(IndividualsExportService);
  modalFormsService = inject(ModalFormsService);
  router: Router = inject(Router);
  toastr = inject(ToastrService);
  modalService = inject(NgbModal);
  destroyRef: DestroyRef = inject(DestroyRef);
  tagsService = inject(TagsService);
  organisationSettingsService = inject(OrganisationSettingsService);

  readonly pageTitle = 'Individuals';
  readonly EntityType = EntityType;
  readonly modalOpened = this.modalFormsService.modalOpened;
  override readonly headerData: IPartialHeaderData = {
    title: this.pageTitle,
    iconClass: 'ic-individuals-20'
  };

  selectedIndividuals: IndividualRecord[] = [];

  alerts: Alert[] = [];
  alertsVisible = false;
  entityForAlerts: IndividualRecord | undefined | null = null;

  notesVisible = false;
  entityForNotes: IndividualRecord | undefined | null = null;

  tagsList: string[] = [];
  showIndividualVerification = false;

  shouldRefreshGridData = false;

  override ngOnInit() {
    super.ngOnInit();
    this.tagsService.getTags()
      .subscribe({
        next: (tags) => {
          this.tagsList = Array.from(new Set(tags?.map(tag => tag.name)));
          this.setGridFilterOptions();
          this.setColDefs();
          this.isLoading.set(false);
        },
        error: (error) => {
          console.warn('[getTags] error: ', error);
        }
      });

    this.individualGridService.refreshFiltersTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef), delay(500))
      .subscribe(() => {
        this.individualGridService.gridApi.refreshServerSide();
        this.setColDefs();
        this.setGridFilterOptions();
      });

    // this.organisationSettingsService.getOrganisationSettings()
    //   .subscribe({
    //     next: (settings) => {
    //       if (settings) {
    //         this.showIndividualVerification = settings.individualVerificationEnabled ?? false;
    //         this.setGridConfig();
    //       }
    //     },
    //     error: (error) => {
    //       console.warn('[getTags] getOrganisationSettings: ', error);
    //     }
      //});
  }

  setGridFilterOptions(): void {
    this.individualGridService.setGridFilterOptions(this.tagsList, this.showIndividualVerification);
  }

  setColDefs(): void {
    this.individualGridService.setColDefs(
      {
        field: '',
        width: 120,
        sortable: false,
        cellClass: 'actions-button-cell',
        cellRenderer: AgIndividualActionsComponent,
        cellRendererParams: {
          open: this.openIndividual.bind(this),
          openAlerts: this.openAlerts.bind(this),
          openNotes: this.openNotes.bind(this),
        },
        menuTabs: ['columnsMenuTab'],
        columnChooserParams: {
          suppressColumnFilter: true,
          suppressColumnExpandAll: true,
          suppressColumnSelectAll: true,
        }
      }
    );
  }

  mergeIndividuals() {
    const individualToMerge = this.selectedIndividuals;

    if (individualToMerge.length < 2) {
      this.toastr.warning("Please select at least 2 individuals to merge");
      return;
    }

    const baseFirstLower = individualToMerge[0].firstName.trim().toLowerCase();
    const baseLastLower = individualToMerge[0].lastName.trim().toLowerCase();

    const anyMismatch = individualToMerge.some(rec => {
      return (
        rec.firstName.trim().toLowerCase() !== baseFirstLower ||
        rec.lastName.trim().toLowerCase() !== baseLastLower
      );
    });

    if (anyMismatch) {
      this.toastr.warning("You can merge individuals only with the same first and last name");
      return;
    }

    const baseMiddle1Lower = individualToMerge?.find(x => !!x.middleName1?.trim())
      ?.middleName1 ?? ''
      .trim().toLowerCase() ?? '';
    const baseMiddle2Lower = individualToMerge?.find(x => !!x.middleName2?.trim())
      ?.middleName2 ?? ''
      .trim().toLowerCase() ?? '';

    let middleNameMismatch = false;

    if (baseMiddle1Lower || baseMiddle2Lower) {
      middleNameMismatch = individualToMerge.some(rec => {
        if (!rec.middleName1 || !rec.middleName2)
          return false;

        if (baseMiddle1Lower && rec.middleName1?.trim().toLowerCase() !== baseMiddle1Lower) {
          return true;
        }

        if (baseMiddle2Lower && rec.middleName2?.trim().toLowerCase() !== baseMiddle2Lower) {
          return true;
        }

        return false;
      });
    }

    if (middleNameMismatch) {
      const modalRef = this.modalService.open(ConfirmModalComponent);
      const componentInstance = modalRef.componentInstance as ConfirmModalComponent;

      componentInstance.title = 'Merge warning';
      componentInstance.message = 'The records selected appear to have different middle names. Are you sure you want to proceed?';
      componentInstance.confirmText = 'Yes';
      componentInstance.closeText = 'No';
      componentInstance.confirm = () => {
        this.openMergeModal();
        return of(true);
      };
    } else {
      this.openMergeModal();
    }
  }

  openMergeModal() {
    const modalRef = this.modalService.open(IndividualMergeModalComponent);
    const componentInstance = modalRef.componentInstance as IndividualMergeModalComponent;

    componentInstance.individualRecords = this.selectedIndividuals;

    modalRef.result.then(
      () => {
        this.individualGridService.gridApi.deselectAll();
        this.individualGridService.gridApi.refreshServerSide();
      },
      doNothing
    );
  }

  manageTags(): void {
    if (this.modalOpened()) return;
    const modalRef = this.modalService.open(
      ManageTagsModalComponent,
      {
        windowClass: 'manage-tags-modal',
        size: 'xl',
        centered: true
      });
    const instance = modalRef.componentInstance as ManageTagsModalComponent;

    instance.entities = this.selectedIndividuals;
    instance.confirm
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.individualGridService.gridApi.deselectAll();
        this.individualGridService.gridApi.refreshServerSide();
      });
  }

  async openIndividual(individual: IndividualRecord): Promise<void> {
    await this.router.navigate(getIndividualProfilePath(individual.individualId));
  }

  openAlerts(individual: IndividualRecord): void {
    if (this.modalOpened()) return;

    this.alertsVisible = true;
    this.alerts = individual?.alerts;
    this.entityForAlerts = individual;
  }

  openNotes(individual: IndividualRecord): void {
    if (this.modalOpened()) return;

    this.notesVisible = true;
    this.entityForNotes = individual;
  }

  onAlertReaded(): void {
    this.shouldRefreshGridData = true;
  }

  onAlertsClose(): void {
    this.alertsVisible = false;

    if (this.shouldRefreshGridData) {
      this.individualGridService.gridApi.refreshServerSide();
    }

    this.shouldRefreshGridData = false;
  }

  // =====  Grid  =====

  readonly defaultPageSize = PageSizeService.defaultPageSize;
  readonly cacheBlockSize = 20;
  readonly pinFirstColumnName = 'Name'
  readonly getRowId: GetRowIdFunc = (params: GetRowIdParams<IndividualRecord>) => params.data.individualId;

  colDefs = this.individualGridService.colDefs;
  totalRecords = this.individualGridService.totalRecords;
  dataSource = this.individualGridService.dataSource;

  onGridReady(gridApi: GridApi): void {
    this.individualGridService.setGridApi(gridApi);
  }

  selectIndividuals(individuals: IndividualRecord[]): void {
    this.selectedIndividuals = individuals;
  }

  onSearch(searchText: string): void {
    this.individualGridService.setDataSource(searchText);
  }

  // =====  Export  =====

  readonly excelStyles = this.individualsExportService.excelStyles;
  readonly excelExportParams = this.individualsExportService.exportParamsXls();
  readonly csvExportParams = this.individualsExportService.exportParamsCsv();

  exportSelectedIndividualsToXls(): void {
    this.exportIndividualList(true, ExportTypeEnum.EXCEL);
  }

  exportSelectedIndividualsToCsv(): void {
    this.exportIndividualList(true, ExportTypeEnum.CSV);
  }

  exportIndividualList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    if (this.modalOpened()) return;

    this.individualsExportService.exportIndividualList(
      this.individualGridService.gridApi,
      this.colDefs(),
      this.selectedIndividuals,
      this.totalRecords(),
      isBulkExport,
      exportType
    );
  }
}
